import { TranslateFunc } from '../../common/types';

import KitLocale, {
  KitLocaleInfo,
  KitRegionInfo,
  KIT_LOCALE_DEFAULT_LOCALE_CODE,
  KIT_LOCALE_DEFAULT_REGION_CODE,
} from '../../common/utils/KitLocale';

// NOTE: This is the result that comes back from picking a language from a region in the KitLocalePicker
export interface KitLocalePickerResult extends KitLocaleInfo {
  // KitLocaleConfig
  privacyPolicyPath: string;
  signupPath: string;

  // KitRegionInfo
  regionCode: string;
  regionName: string;
  ssoSubdomain: string;
  defaultLocaleCode: string;
  displayCaliforniaPrivacy: boolean;
  californiaPrivacyPath: string;

  // extends KitLocaleInfo
  // ...
}

export function getKitLocalePickerResult(
  t: TranslateFunc,
  regionCode: string,
  localeCode: string
): KitLocalePickerResult {
  const localeConfig = KitLocale.getKitLocaleConfig(t);
  let region: KitRegionInfo | undefined;
  let locale: KitLocaleInfo | undefined;

  region = localeConfig.regions.find((r) => {
    return r.regionCode === regionCode;
  });

  if (region) {
    locale = region.locales.find((l) => {
      return l.localeCode === localeCode;
    });
  } else {
    localeConfig.regions.forEach((r) => {
      if (!locale) {
        locale = r.locales.find((l) => {
          return l.localeCode === localeCode;
        });
        if (locale) {
          region = r;
        }
      }
    });
  }

  if (locale === undefined) {
    // If we asked for a locale without regionCode, and it was not found, then this locale is not in our system at all.
    // So call again with NA-US, expecting it to, once again NOT find the locale, but at least now have a region
    if (!regionCode) {
      return getKitLocalePickerResult(t, 'NA-US', localeCode);
    }

    // If the locale is not found for that region, return the default config for that region
    return getKitLocalePickerResult(t, regionCode, region.defaultLocaleCode);
  }

  const { hasOwnProperty } = Object.prototype;

  return {
    privacyPolicyPath: localeConfig.privacyPolicyPath,
    signupPath: localeConfig.signupPath,
    regionCode: region.regionCode,
    regionName: region.regionName,
    ssoSubdomain: region.ssoSubdomain,
    defaultLocaleCode: region.defaultLocaleCode,
    displayCaliforniaPrivacy: hasOwnProperty.call(region, 'displayCaliforniaPrivacy')
      ? region.displayCaliforniaPrivacy
      : false,
    californiaPrivacyPath: hasOwnProperty.call(region, 'californiaPrivacyPath')
      ? region.californiaPrivacyPath
      : '',
    ...locale,
  };
}

export function getDefaultKitLocalePickerResult(t: TranslateFunc): KitLocalePickerResult {
  return getKitLocalePickerResult(
    t,
    KIT_LOCALE_DEFAULT_REGION_CODE,
    KIT_LOCALE_DEFAULT_LOCALE_CODE
  );
}
